import React from 'react';
import type { Task } from '../../types';
import type { ClaimOption } from './claimOptions';

/**
 * Leaves shared by the desktop and mobile queue views.
 *
 * The two views duplicated ten component pairs — one, `ClaimStatePill`,
 * byte-identical — and had already begun to diverge. Everything here is
 * layout-free on purpose: the two views own layout, and nothing else.
 *
 * A piece that legitimately differs between the two stays in its own view
 * file WITH a comment naming the divergence; a grep gate fails on a component
 * defined in both.
 */

/** Why a claim option cannot be claimed right now. */
export function ClaimStatePill({ label }: { label: string }) {
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
      {label}
    </span>
  );
}

/**
 * The one status-label source for a task in the queue.
 *
 * Labels only. The two views render them differently — the desktop list uses
 * background pills, the mobile list uses coloured text — so tone stays with
 * the view. What must not diverge is what a status is CALLED.
 */
export type QueueTaskState =
  | 'escalated' | 'claimed' | 'in_progress' | 'waiting' | 'under_review'
  | 'rework' | 'approved' | 'archived' | 'available';

export function queueTaskState(task: Task, { showEscalation = false } = {}): QueueTaskState {
  if (showEscalation && task.escalatedAt) return 'escalated';
  switch (task.status) {
    case 'claimed': return 'claimed';
    case 'in_progress': return 'in_progress';
    case 'in_review': return task.reviewerId ? 'under_review' : 'waiting';
    case 'changes_requested': return 'rework';
    case 'approved': return 'approved';
    case 'archived': return 'archived';
    default: return 'available';
  }
}

export const QUEUE_STATE_LABELS: Record<QueueTaskState, string> = {
  escalated: 'Escalated',
  claimed: 'Claimed',
  in_progress: 'In progress',
  waiting: 'Waiting',
  under_review: 'Under review',
  rework: 'Rework',
  approved: 'Approved',
  archived: 'Archived',
  available: 'Available',
};

export function queueTaskStateLabel(task: Task, options?: { showEscalation?: boolean }): string {
  return QUEUE_STATE_LABELS[queueTaskState(task, options)];
}

/** Total tasks a set of claim options exposes. */
export function totalAvailable(options: ClaimOption[]): number {
  return options.reduce((sum, option) => sum + option.availableCount, 0);
}

/** Total tasks the expert may actually claim right now. */
export function totalClaimable(options: ClaimOption[]): number {
  return options.reduce(
    (sum, option) => (option.claimingPaused || option.maxReached ? sum : sum + option.availableCount),
    0,
  );
}

